import type { LocalNotificationSchema } from '@capacitor/local-notifications';

import moment from 'moment';

import { AlarmWithUserAlarm } from 'src/models/alarm';

import { AlarmTranslator } from '../types';

const MAXIMUM_DATE_SECONDS = 8_640_000_000_000;
const SCHEDULING_FINGERPRINT_VERSION = 1;

export type AlarmNotificationCandidate = {
	effectiveAt: number;
	identity: string;
	notification: LocalNotificationSchema;
};

export const createAlarmNotificationCandidate = (
	alarm: AlarmWithUserAlarm,
	frozenNowSeconds: number,
	t: AlarmTranslator
): AlarmNotificationCandidate | null => {
	if (!isSupportedAlarmType(alarm.type)) {
		return null;
	}

	const effectiveAt = getEffectiveAlarmTimeInSeconds(alarm);
	if (effectiveAt === null || effectiveAt <= frozenNowSeconds) {
		return null;
	}

	const identity = alarmIdentity(alarm);

	return {
		effectiveAt,
		identity,
		// Keep the callbacks' existing explicit undefined action fields in the native payload.
		notification: {
			actionTypeId: 'snooze',
			attachments: undefined,
			body: createNotificationBody(alarm, effectiveAt, t),
			extra: {
				alarmSchedulingFingerprint: {
					effectiveAt,
					identity,
					version: SCHEDULING_FINGERPRINT_VERSION,
				},
				id: alarm.id,
				type: alarm.type,
			},
			id: alarmNativeId(alarm),
			schedule: {
				allowWhileIdle: true,
				at: new Date(effectiveAt * 1000),
			},
			sound: undefined,
			title: isTaskAlarmType(alarm.type) ? t('alarm_task') : t('alarm_appointment'),
		} as unknown as LocalNotificationSchema,
	};
};

export const getEffectiveAlarmTimeInSeconds = (alarm: AlarmWithUserAlarm): null | number => {
	const rawEffectiveTime = alarm.userAlarmTimestamp !== null ? alarm.userAlarmTimestamp : alarm.alarmTimestamp;
	return parseAlarmEpochSeconds(rawEffectiveTime);
};

export const parseAlarmEpochSeconds = (rawEffectiveTime: unknown): null | number => {
	const effectiveAt = Number(rawEffectiveTime);

	return Number.isFinite(effectiveAt) &&
		Number.isInteger(effectiveAt) &&
		effectiveAt > 0 &&
		effectiveAt <= MAXIMUM_DATE_SECONDS
		? effectiveAt
		: null;
};

const alarmNativeId = (alarm: AlarmWithUserAlarm): number => {
	return hashCode(alarmIdentity(alarm));
};

export const alarmIdentity = (alarm: Pick<AlarmWithUserAlarm, 'id' | 'type'>): string => {
	return `${isTaskAlarmType(alarm.type) ? 'task' : 'appointment'}#${alarm.id}`;
};

export const isSupportedAlarmType = (type: unknown): type is AlarmWithUserAlarm['type'] => {
	return type === 'appointment' || type === 'task' || type === 'taskOwner' || type === 'taskOwnerAlways';
};

const createNotificationBody = (alarm: AlarmWithUserAlarm, effectiveAt: number, t: AlarmTranslator): string => {
	return `${t('alarm_title_label')}: ${alarm.title}\n${t('alarm_date_label')}: ${moment
		.unix(effectiveAt)
		.format('YYYY-MM-DD')}\n${t('alarm_time_label')}: ${moment.unix(effectiveAt).format('HH:mm')} ${t(
		'alarm_time_suffix'
	)}\n${t('alarm_remind_again_label')}`;
};

const isTaskAlarmType = (type: string): boolean => {
	return type === 'task' || type === 'taskOwnerAlways' || type === 'taskOwner';
};

const hashCode = (value: string): number => {
	return value
		.split('')
		.reduce((previousHash, character) => ((previousHash << 5) - previousHash + character.charCodeAt(0)) | 0, 0);
};
